import { useEffect } from "react";

import { useDashboard } from "../dashboardApi";

const ATTRIBUTE = "data-race-week";
const WINDOW_DAYS = 7;

/**
 * useRaceWeekMode — wires DS2 canon-tokens.css `--motion-race-week` × 1.40
 * multiplier by toggling `data-race-week="true"` on `<html>` when the athlete
 * is within 7 days of their A-priority race.
 *
 * Components opt-in to the urgency tone by reading
 * `transition-duration: calc(var(--motion-base) * var(--motion-race-week, 1))`.
 *
 * Mounted at Layout level so the attribute follows route changes. Cleanup on
 * unmount removes the attribute so it never leaks into a re-login as a
 * different (non-race-week) athlete.
 */
export function useRaceWeekMode(): void {
  const { data: dashboard } = useDashboard();
  const raceDateISO = dashboard?.goal?.race_date ?? null;
  const isRaceGoal = dashboard?.goal?.goal_type === "race";

  useEffect(() => {
    // Dev override — ?race_week=1 forces mode on for visual testing
    // (v151 P2.5 — designers/QA preview without waiting for 0-7d window).
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("race_week") === "1") {
        document.documentElement.setAttribute(ATTRIBUTE, "true");
        return;
      }
    } catch {
      // silent: SSR / restricted location
    }
    if (!isRaceGoal || !raceDateISO) {
      document.documentElement.removeAttribute(ATTRIBUTE);
      return;
    }
    const race = new Date(`${raceDateISO}T00:00:00`);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffDays = Math.round((race.getTime() - today.getTime()) / 86_400_000);
    if (diffDays >= 0 && diffDays <= WINDOW_DAYS) {
      document.documentElement.setAttribute(ATTRIBUTE, "true");
    } else {
      document.documentElement.removeAttribute(ATTRIBUTE);
    }
    return () => {
      document.documentElement.removeAttribute(ATTRIBUTE);
    };
  }, [raceDateISO, isRaceGoal]);
}
